import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
  title: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const navigation = {
    admin: [
      { name: 'Dashboard', path: '/admin', icon: '📊', category: 'Main' },
      { name: 'Students', path: '/admin/students', icon: '👨‍🎓', category: 'People' },
      { name: 'Teachers', path: '/admin/teachers', icon: '👨‍🏫', category: 'People' },
      { name: 'Classes', path: '/admin/classes', icon: '🏫', category: 'Academic' },
      { name: 'Subjects', path: '/admin/subjects', icon: '📚', category: 'Academic' },
      { name: 'Timetable', path: '/admin/timetable', icon: '⏰', category: 'Academic' },
      { name: 'Attendance', path: '/admin/attendance', icon: '✅', category: 'Operations' },
      { name: 'Results', path: '/admin/results', icon: '📝', category: 'Operations' },
      { name: 'Assignments', path: '/admin/assignments', icon: '📋', category: 'Operations' },
      { name: 'Fees', path: '/admin/fees', icon: '💰', category: 'Finance' },
      { name: 'Payments', path: '/admin/payments', icon: '💳', category: 'Finance' },
      { name: 'Notices', path: '/admin/notices', icon: '📢', category: 'Communication' },
    ],
    teacher: [
      { name: 'Dashboard', path: '/teacher', icon: '📊', category: 'Main' },
      { name: 'My Classes', path: '/teacher/classes', icon: '🏫', category: 'Teaching' },
      { name: 'Students', path: '/teacher/students', icon: '👨‍🎓', category: 'Teaching' },
      { name: 'Attendance', path: '/teacher/attendance', icon: '✅', category: 'Daily Tasks' },
      { name: 'Assignments', path: '/teacher/assignments', icon: '📋', category: 'Daily Tasks' },
      { name: 'Results', path: '/teacher/results', icon: '📝', category: 'Assessment' },
      { name: 'Timetable', path: '/teacher/timetable', icon: '⏰', category: 'Schedule' },
      { name: 'Notices', path: '/teacher/notices', icon: '📢', category: 'Communication' },
    ],
    student: [
      { name: 'Dashboard', path: '/student', icon: '📊', category: 'Main' },
      { name: 'My Profile', path: '/student/profile', icon: '👤', category: 'Personal' },
      { name: 'Timetable', path: '/student/timetable', icon: '⏰', category: 'Schedule' },
      { name: 'Attendance', path: '/student/attendance', icon: '✅', category: 'Academic' },
      { name: 'Assignments', path: '/student/assignments', icon: '📋', category: 'Academic' },
      { name: 'Results', path: '/student/results', icon: '📝', category: 'Academic' },
      { name: 'Fees', path: '/student/fees', icon: '💰', category: 'Finance' },
      { name: 'Notices', path: '/student/notices', icon: '📢', category: 'Communication' },
    ],
    parent: [
      { name: 'Dashboard', path: '/parent', icon: '📊', category: 'Main' },
      { name: 'Children', path: '/parent/children', icon: '👨‍👩‍👧‍👦', category: 'Family' },
      { name: 'Attendance', path: '/parent/attendance', icon: '✅', category: 'Academic' },
      { name: 'Assignments', path: '/parent/assignments', icon: '📋', category: 'Academic' },
      { name: 'Results', path: '/parent/results', icon: '📝', category: 'Academic' },
      { name: 'Timetable', path: '/parent/timetable', icon: '⏰', category: 'Schedule' },
      { name: 'Fees', path: '/parent/fees', icon: '💰', category: 'Finance' },
      { name: 'Notices', path: '/parent/notices', icon: '📢', category: 'Communication' },
    ],
  };

  const navItems = user ? navigation[user.role as keyof typeof navigation] : [];

  // Group navigation items by category
  const groupedNavItems = navItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, typeof navItems>);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getRoleColor = (role?: string) => {
    switch(role) {
      case 'admin': return 'bg-purple-600';
      case 'teacher': return 'bg-blue-600';
      case 'student': return 'bg-green-600';
      case 'parent': return 'bg-orange-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Top Navigation Bar */}
      <nav className={`${getRoleColor(user?.role)} text-white shadow-lg fixed w-full top-0 z-50`}>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left Section: Hamburger + School Name */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-colors"
                aria-label="Toggle menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-bold">{user?.schoolName || 'School Portal'}</h1>
                <p className="text-xs opacity-90">{user?.schoolCode || ''}</p>
              </div>
            </div>

            {/* Center Section: Date & Time */}
            <div className="flex flex-col items-center">
              <p className="text-sm font-medium">{formatTime(currentDateTime)}</p>
              <p className="text-xs opacity-90 hidden md:block">{formatDate(currentDateTime)}</p>
              <p className="text-xs opacity-90 md:hidden">
                {currentDateTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            </div>

            {/* Right Section: Dark Mode + User Profile */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-colors"
                aria-label="Toggle dark mode"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs opacity-90 capitalize">{user?.role}</p>
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className={`w-10 h-10 rounded-full ${getRoleColor(user?.role)} border-2 border-white flex items-center justify-center font-bold text-sm hover:opacity-90 transition-opacity`}
                >
                  {getInitials(user?.firstName, user?.lastName)}
                </button>
                
                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className={`absolute right-0 mt-2 w-48 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} rounded-md shadow-lg py-1 z-10`}>
                    <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user?.firstName} {user?.lastName}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} capitalize`}>{user?.role}</p>
                    </div>
                    <Link
                      to={`/${user?.role}/profile`}
                      className={`block px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => setShowProfileMenu(false)}
                    >
                      👤 My Profile
                    </Link>
                    <Link
                      to={`/${user?.role}/settings`}
                      className={`block px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => setShowProfileMenu(false)}
                    >
                      ⚙️ Settings
                    </Link>
                    <button
                      onClick={toggleDarkMode}
                      className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      {isDarkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        logout();
                      }}
                      className={`w-full text-left px-4 py-2 text-sm text-red-600 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar Menu */}
      <div
        className={`fixed left-0 top-16 h-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg transform transition-transform duration-300 ease-in-out z-40 ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } w-64`}
      >
        <div className="py-4 overflow-y-auto h-full">
          <nav className="space-y-6">
            {Object.entries(groupedNavItems).map(([category, items]) => (
              <div key={category}>
                <h3 className={`px-6 text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider mb-2`}>
                  {category}
                </h3>
                <div className="space-y-1">
                  {items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center space-x-3 px-6 py-3 text-sm font-medium transition-colors ${
                          isActive
                            ? `${getRoleColor(user?.role)} text-white`
                            : isDarkMode 
                              ? 'text-gray-300 hover:bg-gray-700'
                              : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="text-lg">{item.icon}</span>
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 top-16"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className={`pt-16 transition-all duration-300 ${isMenuOpen ? 'ml-64' : 'ml-0'}`}>
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-6`}>{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
